import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Seo from "@/components/feature/Seo";
import MediaCard from "@/components/feature/MediaCard";
import { buildSearchResults, getHotMedia } from "@/lib/catalog";

const typeFilters = ["全部", "电视剧", "电影", "资讯"] as const;

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [input, setInput] = useState(query);
  const [activeType, setActiveType] = useState<(typeof typeFilters)[number]>(
    "全部"
  );

  useEffect(() => {
    setInput(query);
    setActiveType("全部");
  }, [query]);

  const allResults = useMemo(() => buildSearchResults(query), [query]);
  const results = useMemo(
    () =>
      activeType === "全部"
        ? allResults
        : allResults.filter((r) => r.type === activeType),
    [allResults, activeType]
  );
  const hotPicks = useMemo(() => getHotMedia(4), []);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    setSearchParams(q ? { q } : {});
  };

  const countByType = (type: string) =>
    type === "全部"
      ? allResults.length
      : allResults.filter((r) => r.type === type).length;

  return (
    <SiteLayout>
      <Seo
        title="搜索结果 - 唐心"
        description={`唐心搜索结果：围绕“${query}”为你找到相关影视作品与资讯，包含评分、剧情介绍与分类标签。`}
        keywords="唐心,搜索,影视搜索,电视剧,电影,资讯"
        canonicalPath="/search"
      />

      <div className="w-full px-4 md:px-6 pt-6">
        <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-400">
          <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-600">搜索结果</span>
        </nav>

        <div className="mt-4 rounded-lg bg-background-100 p-5 ring-1 ring-background-200 md:p-6">
          <h1 className="text-xl font-bold text-foreground-950 font-heading md:text-2xl">
            搜索影视内容
          </h1>
          <form
            onSubmit={submit}
            className="mt-4 flex items-center gap-2 rounded-full bg-background-50 px-4 py-2.5 ring-1 ring-background-200 focus-within:ring-primary-300 transition-all"
          >
            <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
              <i className="ri-search-line text-lg"></i>
            </span>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入影视名称、导演、演员或关键词"
              className="min-w-0 flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
            />
            <button
              type="submit"
              className="whitespace-nowrap rounded-full bg-primary-600 px-5 py-1.5 text-sm font-medium text-background-50 hover:bg-primary-700 transition-colors cursor-pointer"
            >
              搜索
            </button>
          </form>

          {query ? (
            <p className="mt-4 text-sm text-foreground-500">
              关于 “<span className="font-medium text-foreground-900">{query}</span>
              ” 的搜索结果，共找到{" "}
              <span className="font-label font-semibold text-accent-600">
                {allResults.length}
              </span>{" "}
              条内容
            </p>
          ) : (
            <p className="mt-4 text-sm text-foreground-500">
              输入关键词开始搜索，支持影视名称、导演、演员与类型模糊匹配。
            </p>
          )}

          {allResults.length > 0 ? (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {typeFilters.map((type) => {
                const active = activeType === type;
                const count = countByType(type);
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setActiveType(type)}
                    className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
                      active
                        ? "bg-primary-600 text-background-50"
                        : "bg-background-50 text-foreground-600 ring-1 ring-background-200 hover:bg-background-200"
                    }`}
                  >
                    {type}（{count}）
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>

      <div className="w-full px-4 md:px-6 mt-6">
        {query && results.length > 0 ? (
          <div className="flex flex-col gap-4">
            {results.map((result) => (
              <Link
                key={`${result.type}-${result.id}`}
                to={result.href}
                className="group flex flex-col gap-4 rounded-lg bg-background-100 p-4 ring-1 ring-background-200 transition-colors hover:bg-background-50 cursor-pointer sm:flex-row animate-fade-up sm:items-center"
              >
                <div className="w-full shrink-0 overflow-hidden rounded-md sm:w-40">
                  <div className={`w-full ${result.type === "资讯" ? "aspect-[16/9]" : "aspect-[16/9] sm:aspect-[3/2]"}`}>
                    <img
                      src={result.poster}
                      alt={result.title}
                      title={`${result.title} 搜索结果`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-md bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700">
                      {result.type}
                    </span>
                    <span className="text-xs text-foreground-400">
                      {result.year}
                    </span>
                    {result.rating > 0 ? (
                      <span className="inline-flex items-center gap-1 font-label text-xs font-semibold text-accent-600">
                        <i className="ri-star-fill"></i>
                        {result.rating.toFixed(1)}
                      </span>
                    ) : null}
                  </div>
                  <h2 className="mt-2 text-base font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-1">
                    {result.title}
                  </h2>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-500 line-clamp-2">
                    {result.summary}
                  </p>
                </div>
                <span className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-full text-foreground-400 group-hover:bg-primary-600 group-hover:text-background-50 transition-colors sm:flex">
                  <i className="ri-arrow-right-line"></i>
                </span>
              </Link>
            ))}
          </div>
        ) : query ? (
          <div>
            <div className="flex flex-col items-center justify-center rounded-lg bg-background-100 py-16 ring-1 ring-background-200">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-3xl text-foreground-400">
                <i className="ri-search-eye-line"></i>
              </span>
              <p className="mt-4 text-base font-medium text-foreground-800">
                没有找到相关内容
              </p>
              <p className="mt-1 px-4 text-center text-sm text-foreground-500">
                换个关键词试试，或看看下面的热门推荐
              </p>
            </div>
            <div className="mt-10">
              <h2 className="text-lg font-bold text-foreground-950 font-heading">
                热门影视推荐
              </h2>
              <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                {hotPicks.map((item, index) => (
                  <MediaCard key={item.id} item={item} index={index} />
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </SiteLayout>
  );
}